import type { CourseDiff, CourseDiffSectionChange, CoursePublishDecisions } from '../../../../../../../../api';
import type {
  LearnerWhatsNew,
  LessonPartChange,
  PartChange,
  Requirement
} from '../../../../../../../../components/course-versioning/learnerWhatsNew';

/**
 * Course versioning, admin side: the publish diff as "what changed in each
 * lesson" — the parts (content, case studies, quiz, quiz questions, live
 * scenario, feedback questions) that were added, edited or removed. The
 * Updates list and the example What's New notice both read it.
 */

const SECTION_CHANGE: Record<CourseDiffSectionChange, PartChange> = {
  added: 'added',
  enabled: 'added',
  changed: 'edited',
  removed: 'removed',
  disabled: 'removed'
};

const unique = (ids: string[]): string[] => Array.from(new Set(ids));

/** Lesson id → the parts that changed in it, in a fixed reading order. Lessons added or removed whole aren't listed. */
export function diffLessonParts(diff: CourseDiff): Map<string, LessonPartChange[]> {
  const parts = new Map<string, LessonPartChange[]>();
  const add = (lessonId: string, p: LessonPartChange) => parts.set(lessonId, [...(parts.get(lessonId) || []), p]);

  diff.lessons.textChanged.forEach((l) => add(l.id, { part: 'content', change: 'edited' }));
  diff.sections.forEach(
    (s) => s.caseStudies && add(s.lessonId, { part: 'caseStudies', change: SECTION_CHANGE[s.caseStudies] })
  );
  // A quiz switched on/off reads as the quiz added/removed, not each of its questions.
  const quizSwitch = new Map(diff.sections.filter((s) => s.quiz).map((s) => [s.lessonId, s.quiz]));
  quizSwitch.forEach((sw, id) => add(id, { part: 'quiz', change: sw === 'enabled' ? 'added' : 'removed' }));
  diff.quiz.forEach((q) => {
    const sw = quizSwitch.get(q.lessonId);
    const edited = unique([...q.rewritten, ...q.answerKeyChanged]).filter(
      (id) => !q.added.includes(id) && !q.removed.includes(id)
    );
    if (edited.length) add(q.lessonId, { part: 'quizQuestions', change: 'edited', count: edited.length });
    if (q.added.length && sw !== 'enabled')
      add(q.lessonId, { part: 'quizQuestions', change: 'added', count: q.added.length });
    if (q.removed.length && sw !== 'disabled') {
      add(q.lessonId, { part: 'quizQuestions', change: 'removed', count: q.removed.length });
    }
  });
  diff.sections.forEach(
    (s) => s.simulation && add(s.lessonId, { part: 'liveScenario', change: SECTION_CHANGE[s.simulation] })
  );
  (diff.feedback || []).forEach((f) => {
    if (f.rewritten.length) add(f.lessonId, { part: 'feedbackQuestions', change: 'edited', count: f.rewritten.length });
    if (f.added.length) add(f.lessonId, { part: 'feedbackQuestions', change: 'added', count: f.added.length });
    if (f.removed.length) add(f.lessonId, { part: 'feedbackQuestions', change: 'removed', count: f.removed.length });
  });
  return parts;
}

const questionLabel = (n: number, noun: string) => (n === 1 ? noun : `${n} ${noun}s`);

/** The admin Updates list wording: "Lesson Body updated", "2 Quiz Questions added", "Roleplay edited". */
export function partLabel(p: LessonPartChange): string {
  const n = p.count ?? 1;
  switch (p.part) {
    case 'content':
      return p.change === 'removed' ? 'Lesson Body removed' : 'Lesson Body updated';
    case 'caseStudies':
      return `Case Studies ${p.change}`;
    case 'quiz':
      return `Quiz ${p.change}`;
    case 'quizQuestions':
      return `${questionLabel(n, 'Quiz Question')} ${p.change}`;
    case 'liveScenario':
      return `Roleplay ${p.change}`;
    case 'feedbackQuestions':
      return `${questionLabel(n, 'Feedback Question')} ${p.change}`;
    default:
      return '';
  }
}

export type ExampleLearner = 'inProgress' | 'completed';

/**
 * Whether a learner has to redo a changed part under the chosen decisions —
 * the BE policy's rules. Reading content, case studies and feedback questions
 * follow `textChanged`; the quiz follows `quizChanged`; the live scenario
 * `scenarioChanged`. Removed parts never need anything.
 */
const partRequired = (p: LessonPartChange, d: CoursePublishDecisions): boolean => {
  if (p.change === 'removed') return false;
  switch (p.part) {
    case 'content':
    case 'caseStudies':
    case 'feedbackQuestions':
      return d.textChanged === 'reread';
    case 'quiz':
    case 'quizQuestions':
      return d.quizChanged === 'require';
    case 'liveScenario':
      return d.scenarioChanged === 'require';
    default:
      return false;
  }
};

/**
 * The What's New one learner would get for this publish, for the admin's
 * read-only example. Assumes the learner had already completed every changed
 * lesson (so each one counts as updated for them); the two example learners
 * differ in whether new lessons are required, under "Optional for Completers".
 */
export function exampleWhatsNew(
  diff: CourseDiff,
  decisions: CoursePublishDecisions,
  learner: ExampleLearner,
  /** The draft's lessons, id → title, in course order. */
  lessonTitles: Map<string, string>
): LearnerWhatsNew {
  const order = Array.from(lessonTitles.keys());
  const rank = (id: string) => (order.includes(id) ? order.indexOf(id) : order.length);
  const byCourseOrder = <T extends { taskId: string }>(a: T, b: T) => rank(a.taskId) - rank(b.taskId);

  const newRequirement: Requirement =
    decisions.newItems === 'required_all' ||
    (decisions.newItems === 'required_in_progress_optional_completers' && learner === 'inProgress')
      ? 'required'
      : 'optional';
  const newLessons = diff.lessons.added
    .map((l) => ({
      taskId: l.id,
      title: lessonTitles.get(l.id) || l.title,
      requirement: newRequirement,
      parts: []
    }))
    .sort(byCourseOrder);

  const removed = new Set(diff.lessons.removed.map((l) => l.id));
  const updatedLessons = Array.from(diffLessonParts(diff).entries())
    .filter(([id]) => !removed.has(id))
    .map(([id, changes]) => {
      const parts = changes.map((p) => ({ ...p, required: partRequired(p, decisions) }));
      return {
        taskId: id,
        title: lessonTitles.get(id) || 'Untitled lesson',
        requirement: (parts.some((p) => p.required) ? 'required' : 'optional') as Requirement,
        parts
      };
    })
    .sort(byCourseOrder);
  return { newLessons, updatedLessons };
}
